/**
 * `firm-client-document` (crossmarc/steward-app#1769, docs/35-advisor-client-onboarding.md) —
 * a firm's own document library on one prospect's or client's profile. Never shared with the
 * family, even after Steward is set up for them: docs/35 says it plainly, "About notes and
 * firm documents are the firm's and are not copied."
 *
 * ## Placement and shape
 *
 * Lives in `STEWARD_ADVISOR_FIRM_TYPE_DEFS`, the same pack as `advisor-client` and
 * `client-about`, and for the same reason `client-about`'s header gives: it is firm data
 * about a client, keyed to an `advisor-client` row, and a client workspace (if one is ever
 * set up) carries no membership in the firm's own workspace — so there is no read path from
 * the family side at all, independent of anything this file declares.
 *
 * Unlike `client-about`, this type is FILE-BACKED (`behaviors: ['file-backed']`,
 * `fileBacked.keyPrefix: 'firm-client-documents'`) — its own prefix, not `document`'s
 * `'documents'`. `GET /documents` (the family vault's list route) lists everything under the
 * `document` type code only, so reusing that type would put a firm's private upload in a
 * family member's own vault list the moment one exists. A distinct type code and a distinct
 * S3 prefix is what keeps the two libraries from ever colliding, in either direction.
 *
 * `accessControl.writeCapability` is `STEWARD_WRITE_CAPABILITY`, the SAME union `advisor-client`
 * and `client-about` document: no role in THIS pack carries `steward:write`, but a firm
 * workspace mounts `steward-app` too, and `steward-admin`/`steward-member` there do
 * (`packRoles.ts`). That is the ordinary appcore-level gate (Tier 1/2: `inst:write` plus this
 * capability). It is NOT the team gate — see below.
 *
 * ## The team gate is the FAÇADE's, not this type's
 *
 * Decided (#1769 D1): only a firm admin or the client's own assigned advisor may reach this
 * client's documents at all — every other firm member, even one holding ordinary appcore
 * read/write on this type, must get a 403. appcore's `accessControl` has no primitive for
 * "custody of a specific row" (that is `resolveClientIdentityAuthority`, a DIFFERENT appcore
 * mechanism keyed off the `advisor-client` roster, not a `CustomType` declaration), so this
 * type declares no `readCapability` and relies entirely on
 * `services/steward-app-api/src/handlers/firmClientDocuments.ts` calling
 * `GET /w/{ws}/advisor-clients/{clientId}/access` (appcore's feat/advisor-client-access-probe
 * (commit 1624aed, not yet merged)) before every read or write, live, with no cache.
 *
 * ⚠️ A caller who reaches appcore's GENERIC routes directly, bypassing this façade, would be
 * bound only by the ordinary Tier 1/2 gate above — and that gate is NOT symmetric: a direct
 * write would need `steward:write`, but declaring no `readCapability` means a direct read would
 * need only `inst:read` plus Tier-3, appcore's `custom-type.ts` treating an unset
 * `readCapability` as "readable by anyone holding `inst:read`", a much lower bar. **This is a
 * residual, not a live bypass on any deployed instance (independent review, 2026-09-25):** all
 * three committed profiles set `"memberPlane": "relay-only"`, which puts those generic routes
 * behind appcore's IAM (SigV4) authorizer rather than the Cognito JWT one a browser session
 * holds — see `services/steward-app-api/src/handlers/firmClientDocuments.ts`'s header for the
 * exact citations and what WOULD have to change (a future caller-type-forwarding façade route,
 * or an instance profile flipped back to `direct`) for it to become reachable. Same known class
 * `client-about`'s header names for itself, not fixed here for the same reason: no per-row
 * custody primitive exists at appcore today (see the handler's own header for the two rejected
 * alternatives, and for the upload/download URL TTL and orphan-object facts that bound the
 * unassign-then-use-a-live-URL case separately from this one).
 *
 * ## One row per document, found by clientId attribute — same shape as `client-about`
 *
 * appcore's file-backed create mints its own `entityId` (the upload-mint receipt, appcore#672)
 * and takes no caller-supplied id otherwise, so a document is not "keyed by" `clientId` the way
 * a direct `getEntity` lookup would want. The façade lists the firm's `firm-client-document`
 * rows and filters by the `clientId` attribute (`listFirmDocuments` in the handler). UNLIKE
 * `client-about`, many rows share one `clientId` — every document is its own row — so there is
 * no upsert-in-place question here.
 *
 * A row whose `clientId` attribute does not match the path's `clientId` must read as 404, the
 * same as a `clientId` naming no `advisor-client` row at all: the handler re-checks the
 * attribute on every single-document read/download/delete, never trusting the entityId alone.
 */
import { z } from 'zod';

export const FIRM_CLIENT_DOCUMENT_TYPE_CODE = 'firm-client-document';

/** appcore's `fileBacked.keyPrefix` for this type — its own namespace, not `document`'s
 *  `'documents'`. See this file's header, "Placement and shape". */
export const FIRM_CLIENT_DOCUMENT_KEY_PREFIX = 'firm-client-documents';

const id = z.string().min(1).max(200);
const instant = z.iso.datetime();

export const FirmClientDocumentAttributesSchema = z.object({
  firmId: id,
  /** The `advisor-client` row this document belongs to. */
  clientId: id,
  title: z.string().min(1).max(200),
  filename: z.string().min(1).max(300),
  mimeType: z.string().min(1).max(200),
  fileSize: z.number().int().nonnegative(),
  /** Server-derived by appcore; never trusted from a client. Present on every row appcore
   *  file-backed create ever writes, but not part of what a caller sends. */
  s3Key: z.string().min(1).optional(),
  /** Whoever uploaded it, server-stamped from the caller's own identity (never client-supplied;
   *  the same rule `clientAbout.ts`'s `source.name` follows). For a firm member's upload that is
   *  a display name. For a row the client added (`source` is `'client'`) it is the client's
   *  grant email address, so it is not a name: render it as an address. */
  addedByName: z.string().min(1).max(200),
  addedByUserId: id,
  createdAt: instant,
  /** `'client'` on a row the client added through appcore's onboarding-document routes
   *  (`/me/onboarding-forms/{firm}/requests/{requestId}/documents`, which stamp it with the
   *  constant). ABSENT on every row a firm member added through this façade, which stamps
   *  neither field, so an absent `source` means firm-added ({@link isClientAddedDocument}).
   *  A value this schema cannot use (empty, over 50 characters, not a string) reads as ABSENT
   *  rather than failing the parse: a label must never make a stored file vanish from the list
   *  and 404 on download and delete while the row still exists. */
  source: z.string().min(1).max(50).optional().catch(undefined),
  /** The information request the client filed it under, a label the client's request chose:
   *  appcore checks its grammar only (`^[A-Za-z0-9_-]{1,64}$`) and never that it names a
   *  request, so a reader must match it against the client's requests row. Same rule for an
   *  unusable value: it reads as absent. Nothing here ties it to `source`; a row can carry
   *  either alone. */
  requestId: z.string().min(1).max(64).optional().catch(undefined),
});

export type FirmClientDocumentAttributes = z.infer<typeof FirmClientDocumentAttributesSchema>;

/** The value of `source` appcore stamps on a client-added row. */
export const FIRM_CLIENT_DOCUMENT_SOURCE_CLIENT = 'client';

/** The value of `source` on a row pulled from a linked bank (Plaid statements). */
export const FIRM_CLIENT_DOCUMENT_SOURCE_PLAID = 'plaid';

/** Whether the client added this row. An exact match on `'client'`: absent, `'plaid'` or any other
 *  `source` is NOT client-added. A bank-fetched row is the bank's, not something the client
 *  uploaded, so it is not labelled as theirs ({@link isBankStatementDocument}). */
export function isClientAddedDocument(source: string | undefined): boolean {
  return source === FIRM_CLIENT_DOCUMENT_SOURCE_CLIENT;
}

/** What the firm's Documents tab calls a row pulled from a linked bank. Wording provisional
 *  (the founder may change it); this is the one place the text lives. */
export const BANK_STATEMENT_DOCUMENT_LABEL = 'Bank statement';

/** Whether this row was pulled from a linked bank. Exact match on `'plaid'`, and never true for
 *  a row {@link isClientAddedDocument} accepts. */
export function isBankStatementDocument(source: string | undefined): boolean {
  return source === FIRM_CLIENT_DOCUMENT_SOURCE_PLAID;
}

/** Reads a stored `firm-client-document` row's attributes. `unreadable` covers both "does not
 *  parse" and "is missing", matching `readClientAbout`'s and `readAdvisorClient`'s convention:
 *  one broken row is skipped, never taken down the whole list. */
export function readFirmClientDocument(
  value: unknown,
): { kind: 'document'; document: FirmClientDocumentAttributes } | { kind: 'unreadable' } {
  const parsed = FirmClientDocumentAttributesSchema.safeParse(value);
  return parsed.success ? { kind: 'document', document: parsed.data } : { kind: 'unreadable' };
}
